import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

/**
 * Hash-driven view router for the House of Numbardar SPA.
 *
 * Mirrors the architecture brief literally:
 *   • One <div class="view"> per route in App.js
 *   • Only the active view is shown (display + opacity toggled)
 *   • URL hash (`#dealroom`, `#estates`, …) reflects the active view
 *   • Browser back / forward / refresh restores the right view
 *
 * Modal state is kept here too because "Consult the House" overlays the
 * current view rather than replacing it.
 */
export const VIEWS = [
  "home",
  "dealroom",
  "estates",
  "capital",
  "atelier",
];
const DEFAULT = "home";

const parseHash = () => {
  if (typeof window === "undefined") return DEFAULT;
  const raw = window.location.hash.replace(/^#/, "");
  return VIEWS.includes(raw) ? raw : DEFAULT;
};

const ViewContext = createContext({
  view: DEFAULT,
  showView: () => {},
  contactModalOpen: false,
  openContactModal: () => {},
  closeContactModal: () => {},
});

export function ViewRouterProvider({ children }) {
  const [view, setView] = useState(parseHash);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    const onHashChange = () => {
      const next = parseHash();
      setView(next);
      // Each view starts scrolled to its top — like switching screens.
      window.scrollTo(0, 0);
      const el = document.getElementById(next);
      if (el) el.scrollTop = 0;
    };
    window.addEventListener("hashchange", onHashChange);
    // Ensure scroll position is reset on initial deep-link too.
    if (parseHash() !== DEFAULT) window.scrollTo(0, 0);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const showView = useCallback((id) => {
    if (!VIEWS.includes(id)) return;
    const current = parseHash();
    if (current === id) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Setting the hash fires hashchange, which updates state.
    window.location.hash = id;
  }, []);

  const openContactModal = useCallback(() => {
    setContactModalOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeContactModal = useCallback(() => {
    setContactModalOpen(false);
    document.body.style.overflow = "";
  }, []);

  return (
    <ViewContext.Provider
      value={{
        view,
        showView,
        contactModalOpen,
        openContactModal,
        closeContactModal,
      }}
    >
      {children}
    </ViewContext.Provider>
  );
}

export const useViewRouter = () => useContext(ViewContext);
